import { InputField, Select } from "kubra-ux-forge";
import { getWcagAutocompleteValue } from "utilities/wcag";
import React from "react";
import { useField, useFormikContext } from "formik";
import { states } from "constants/us-states";
import { useTranslation } from "react-i18next";
import { COMMON_NAMESPACE } from "constants/i18n-namespaces";

interface AddressFieldProps {
  name: string;
  streetAddressLabel: string;
  localityLabel: string;
  regionLabel: string;
  postalCodeLabel: string;
  editableIfPrefilled: boolean;
}

const AddressField: React.FC<AddressFieldProps> = ({
  name,
  streetAddressLabel,
  localityLabel,
  regionLabel,
  postalCodeLabel,
  editableIfPrefilled,
}) => {
  const [streetAddressField, streetAddressMeta] = useField(
    `${name}.streetAddress`,
  );

  let disabled = false;

  if (streetAddressMeta.initialValue && !editableIfPrefilled) {
    disabled = true;
  }

  const { t } = useTranslation();
  const [localityField, localityMeta] = useField(`${name}.locality`);
  const [regionField, regionMeta] = useField(`${name}.region`);
  const [postalCodeField, postalCodeMeta] = useField(`${name}.postalCode`);
  const { setFieldValue } = useFormikContext();

  const handleRegionChange = (selectedOption: any) => {
    setFieldValue(`${name}.region`, selectedOption[0].name);
  };

  return (
    <div>
      <div style={{ marginBottom: "24px" }}>
        <InputField
          {...streetAddressField}
          aria-required="true"
          errorText={
            streetAddressMeta.touched ? streetAddressMeta.error : undefined
          }
          hasError={
            streetAddressMeta.touched ? !!streetAddressMeta.error : false
          }
          aria-invalid={streetAddressMeta.touched && !!streetAddressMeta.error}
          label={streetAddressLabel}
          dataCy={`${name}.streetAddress`}
          disabled={disabled}
          autoComplete={getWcagAutocompleteValue(
            "streetAddress",
            streetAddressLabel,
          )}
        />
      </div>

      <div className="ez-pay-payable-address-field-row">
        <InputField
          {...localityField}
          aria-required="true"
          errorText={localityMeta.touched ? localityMeta.error : undefined}
          hasError={localityMeta.touched ? !!localityMeta.error : false}
          aria-invalid={localityMeta.touched && !!localityMeta.error}
          label={localityLabel}
          dataCy={`${name}.locality`}
          disabled={disabled}
          autoComplete={getWcagAutocompleteValue("city", localityLabel)}
        />
      </div>
      <div className="ez-pay-payable-address-field-row">
        <Select
          {...regionField}
          aria-required="true"
          defaultValue={regionField.value}
          placeholder={`${t("select", { ns: COMMON_NAMESPACE })}`}
          label={regionLabel}
          dataCy={`${name}.region`}
          onChange={handleRegionChange}
          options={states}
          errorText={regionMeta.touched ? regionMeta.error : undefined}
          hasError={regionMeta.touched ? !!regionMeta.error : false}
          aria-invalid={regionMeta.touched && !!regionMeta.error}
          isMultiSelect={false}
          disabled={disabled}
        />
        <InputField
          {...postalCodeField}
          aria-required="true"
          errorText={postalCodeMeta.touched ? postalCodeMeta.error : undefined}
          hasError={postalCodeMeta.touched ? !!postalCodeMeta.error : false}
          aria-invalid={postalCodeMeta.touched && !!postalCodeMeta.error}
          label={postalCodeLabel}
          dataCy={`${name}.postalCode`}
          disabled={disabled}
          autoComplete={getWcagAutocompleteValue("postalCode", postalCodeLabel)}
        />
      </div>
    </div>
  );
};

export default AddressField;
